import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useOrganizationContext } from '../../contexts/OrganizationContext';
import { fetchOnboardingStatus } from '../../../services/onboardingApi';
import NoOrganizationAccess from './NoOrganizationAccess';

const SUPERADMIN_EMAIL = 'superadmin@agenticpractice.ai';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuthContext();
  const { organizationId, loading: orgLoading } = useOrganizationContext();
  const location = useLocation();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);

  const isBaaAcceptPath = location.pathname === '/legal/baa-accept';
  const isOnboardingPath = location.pathname === '/onboarding';

  useEffect(() => {
    if (!user || user.email === SUPERADMIN_EMAIL) return;
    if (isBaaAcceptPath) return;
    let cancelled = false;
    setCheckingOnboarding(true);
    fetchOnboardingStatus()
      .then((data) => {
        if (!cancelled) setOnboardingStatus(data);
      })
      .catch(() => {
        if (!cancelled) {
          setOnboardingStatus({ onboarding_completed: true, needs_baa: false, baa_accepted: true });
        }
      })
      .finally(() => {
        if (!cancelled) setCheckingOnboarding(false);
      });
    return () => { cancelled = true; };
    // Key on identity, not the whole user object. Onboarding status depends on
    // who the user is, not on unrelated profile fields (e.g. mfa_method). Keying
    // on `user` re-ran this on every setUser(...), flipping checkingOnboarding
    // and showing the full-screen spinner — which unmounted the routed page and
    // discarded in-progress local state (e.g. the 2FA QR enrollment in Settings).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.email, location.pathname, isBaaAcceptPath]);

  if (loading || checkingOnboarding || orgLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.email !== SUPERADMIN_EMAIL && !isBaaAcceptPath && !isOnboardingPath && !organizationId) {
    return <NoOrganizationAccess />;
  }

  if (user.email !== SUPERADMIN_EMAIL) {
    if (
      onboardingStatus?.needs_baa &&
      onboardingStatus?.can_accept_baa &&
      !isBaaAcceptPath
    ) {
      return <Navigate to="/legal/baa-accept" replace />;
    }

    if (
      onboardingStatus &&
      onboardingStatus.requires_onboarding &&
      !isOnboardingPath &&
      !isBaaAcceptPath
    ) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
